/**
 * ⟦A-16⟧ **Le récapitulatif des jours écoulés, mis en mots** — « montrer le calcul, jamais le
 * solde ».
 *
 * Mot de Mathieu : *« il ne faut pas que le user se dise : je suis censé perdre 10 points mais
 * en fait j'en perds que 7, parce qu'il ne voit pas qu'il en perd 10 et qu'il en regagne 3 grâce
 * aux nuits imposées. Il faut bien décrire tout le calcul. »*
 *
 * Ce fichier ne porte **que du vocabulaire** : une cause du moteur → une phrase française, une
 * jauge → une icône et un format. **Aucune règle, aucun recalcul.** Les deltas affichés sont
 * ceux que `RecapLigne.delta` porte, tels que l'état les a réellement encaissés — le moteur les
 * MESURE (`shared/src/engine/recap.ts`), il ne les dérive pas, et le client encore moins.
 *
 * Deux points où la tentation de recalculer était forte, et ce qu'on fait à la place :
 *
 * 1. **Les nuits contenues.** Le joueur voit « +9 ❤ » et croit à un cadeau, alors que c'est une
 *    perte : trois vraies nuits lui auraient rendu 30. On ne calcule pas ce manque à gagner — on
 *    **cite les deux constantes du moteur** (`SM_NUIT_CONTENUE` contre `SM_NUIT`) dans la phrase,
 *    et le joueur fait la soustraction lui-même. C'est exactement « décrire tout le calcul ».
 * 2. **Le solde par jauge.** `soldeParJauge` vient du moteur et n'est affiché qu'**au-dessus** du
 *    détail qui le décompose, jamais à sa place (A-16 l'interdit noir sur blanc).
 *
 * ⚠️ **Ne pas réutiliser l'agrégation du journal** (`journal.ts`, `ligneVersements`) : elle fond
 * les versements en une ligne « sinon dormir trois jours noie le journal ». Ici le détail EST le
 * sujet.
 */

import {
  SM_NUIT,
  SM_NUIT_CONTENUE,
  type ActionType,
  type RecapCause,
  type RecapJauge,
  type RecapLigne,
  type Recapitulatif,
  type Systemes,
} from "@maxime/shared";

import { contenu } from "./content";
import { titreCourt } from "./evenementCourt";
import { nombre } from "./format";
import { interrupteurs } from "./menage";

// ─────────────────────────────────────────────────────────────────────────────
// Les jauges
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Comment chaque jauge suivie s'écrit. Les icônes sont celles du HUD (`Studio.tsx`) : le
 * joueur doit relier une ligne du récapitulatif à la jauge qui vient de bouger sous ses yeux,
 * sans traduction mentale.
 */
export const JAUGE_RECAP: Record<
  RecapJauge,
  { icone: string; nom: string; decimales?: number }
> = {
  abonnes: { icone: "👥", nom: "Abonnés" },
  energie: { icone: "⚡", nom: "Énergie" },
  sante_mentale: { icone: "❤", nom: "Santé mentale" },
  reputation: { icone: "⭐", nom: "Réputation" },
  fidelite: { icone: "🤝", nom: "Fidélité" },
  argent: { icone: "₵", nom: "Clicks" },
  momentum: { icone: "🚀", nom: "Momentum", decimales: 2 },
  inspiration: { icone: "🃏", nom: "Idées en main" },
  xp: { icone: "✨", nom: "XP" },
  pc: { icone: "🌳", nom: "Points" },
  // ⟦SPEC-FIL §9.4⟧ La 8ᵉ jauge. Même icône que le bouton du Fil et que sa jauge du HUD : le
  // joueur doit relier la ligne à ce qui vient de bouger sous ses yeux, sans traduction.
  hype: { icone: "💬", nom: "Hype" },
};

/**
 * ⟦C60 bis · A-177⟧ **Le récapitulatif, sans la jauge que Matt a retirée de l'écran.** Arbitrage n° 2 du
 * 03/10/2026 : *« La réputation : la retirer de l'écran. »* Le moteur mesure toujours ses lignes
 * (l'érosion du chaos, la récompense de la ligne lumière, le silence : `jauge === "reputation"`), elles
 * ne se lisent plus. Rien n'est supprimé, rien n'est recalculé : on ne MONTRE pas ces lignes, c'est tout.
 *
 * Interrupteur allumé (ou contenu qui ne dit rien) : **le même objet**, sans copie, le jeu d'avant au
 * caractère près. Pur : `sys` se passe en paramètre, la valeur par défaut lit le contenu chargé.
 */
export function sansReputation(
  recap: Recapitulatif,
  sys: Pick<Systemes, "reputation"> = interrupteurs(),
): Recapitulatif {
  if (sys.reputation) return recap;
  return { ...recap, lignes: recap.lignes.filter((l) => l.jauge !== "reputation") };
}

/** « +25 » / « −0,04 » — signe explicite, virgule française, jamais de « +0 ». */
export function deltaLisible(jauge: RecapJauge, delta: number): string {
  const { decimales } = JAUGE_RECAP[jauge];
  const signe = delta > 0 ? "+" : "−";
  const valeur =
    decimales === undefined
      ? nombre(Math.abs(delta))
      : Math.abs(delta).toFixed(decimales).replace(".", ",");
  return `${signe}${valeur}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Les causes — le vocabulaire fermé du moteur, en français
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Où chaque cause se range à l'écran. Trois temps, et c'est la structure de l'écran :
 *
 * - `travail` — ce que l'action t'a coûté (elle appartient à l'action, pas à un jour) ;
 * - `monde` — ce qui s'est passé **pendant** ces jours, jour par jour ;
 * - `apres` — ce qu'il en sort à l'arrivée (publication, tuile, progression).
 *
 * ⚠️ C'est un `Record` **exhaustif** à dessein : le jour où le moteur ajoute une cause à son
 * vocabulaire fermé, ce fichier **ne compile plus** tant que personne n'a décidé où elle va et
 * comment elle se dit. C'est ce que `recap.ts` du moteur appelle « une cause inventée ailleurs
 * ne compilerait pas » — le garde-fou est ici.
 */
export const SECTION_CAUSE: Record<RecapCause, "travail" | "monde" | "apres"> = {
  cout_action: "travail",
  crunch: "travail",
  depassement: "travail",
  nuits_contenues: "travail",

  vues_versees: "monde",
  charges: "monde",
  // ⟦SPEC-PRODUCTEUR §6.2 · A-81⟧ Les salaires tombent PENDANT les jours écoulés, à la
  // frontière de jour, comme les charges — même section, même famille.
  salaires: "monde",
  erosion_chaos: "monde",
  // ⟦A-74⟧ La récompense de la ligne lumière : le monde te regarde tenir ta ligne, comme
  // l'érosion est le monde qui te la fait payer — même section, même famille.
  rep_lumiere: "monde",
  rente_evergreen: "monde",
  desabonnements: "monde",
  momentum_silence: "monde",
  idee_perimee: "monde",
  opportunite_expiree: "monde",
  tendance: "monde",
  rival: "monde",
  nuit: "monde",
  // ⟦SPEC-FIL §2.4⟧ La Hype redescend PENDANT les jours écoulés, comme le momentum : c'est la
  // cinquième pression du temps ⟦A-15⟧, elle se range avec les quatre autres.
  fil: "monde",

  publication: "apres",
  evenement: "apres",
  progression: "apres",
  effet_action: "apres",
};

/** L'icône qui ouvre la ligne — elle dit la cause d'un coup d'œil. */
export const ICONE_CAUSE: Record<RecapCause, string> = {
  cout_action: "🎬",
  crunch: "🥱",
  depassement: "🕰",
  nuits_contenues: "🛌",
  vues_versees: "📈",
  charges: "🏦",
  salaires: "🎬",
  erosion_chaos: "🌀",
  rep_lumiere: "🌟",
  rente_evergreen: "🌲",
  desabonnements: "💔",
  momentum_silence: "🚀",
  nuit: "🌙",
  idee_perimee: "🗑",
  opportunite_expiree: "🚪",
  tendance: "🔥",
  rival: "👤",
  publication: "▶",
  evenement: "📨",
  progression: "⭐",
  effet_action: "•",
  fil: "💬",
};

/**
 * La cause, en français. `cout_action` est la seule à dépendre de l'action : la table §2.2
 * porte aussi bien un débit (produire) qu'un crédit (dormir rend 80 ⚡ et 10 ❤), et l'appeler
 * « le travail » dans les deux cas serait faux.
 */
export function libelleCause(cause: RecapCause, action: ActionType): string {
  switch (cause) {
    case "cout_action":
      switch (action) {
        case "sleep":
          return "ta nuit";
        case "produce_video":
        case "video_jalon":
          return "le travail sur la vidéo";
        case "veille":
          return "la veille";
        case "lire_commentaires":
          return "les commentaires lus";
        case "pause":
        case "annoncer_pause":
          return "la pause";
        case "therapie":
          return "la séance";
        default:
          return "l'action";
      }
    case "crunch":
      return "le crunch — tu as commencé sous 20 ⚡";
    case "depassement":
      return "les heures de trop";
    case "nuits_contenues":
      return "tes nuits pendant le travail";
    case "vues_versees":
      return "les vues de la journée";
    case "charges":
      return "les charges du jour";
    case "salaires":
      return "les salaires de l'équipe";
    case "erosion_chaos":
      return "ta ligne chaos t'use";
    case "rep_lumiere":
      return "ta ligne lumière paie — lentement";
    case "rente_evergreen":
      return "la rente de tes vidéos intemporelles";
    case "desabonnements":
      return "les désabonnements";
    case "momentum_silence":
      return "le silence — l'algorithme t'oublie";
    case "nuit":
      return "la nuit";
    case "idee_perimee":
      return "une idée s'est périmée";
    case "opportunite_expiree":
      return "une fenêtre s'est refermée";
    case "tendance":
      return "les tendances ont bougé";
    case "rival":
      return "la concurrence a avancé";
    // ⟦SPEC-FIL §9.4⟧ Une seule cause, plusieurs situations : le `detail` du moteur porte
    // laquelle. Le libellé par défaut vise la plus fréquente de loin — la décroissance de nuit.
    case "fil":
      return "ta communauté refroidit";
    case "publication":
      return "la mise en ligne";
    case "evenement":
      return "ce qui t'est tombé dessus";
    case "progression":
      return "ta progression";
    case "effet_action":
      return "l'effet de l'action";
  }
}

/**
 * ⟦A-15 §2⟧ Le `detail` d'une ligne `tendance` est un triplet `trend_id:raison:phase`
 * (`engine/sleep.ts`) — `RecapLigne.detail` est une chaîne unique, le moteur n'a pas d'autre
 * endroit où mettre les trois. Sans cette lecture, l'écran affichait tout cru :
 * « La tendance « challenge_caritatif:phase:pic » a changé de phase. »
 *
 * Rend `null` si la forme n'est pas celle-là — on retombe alors sur l'identifiant, jamais sur
 * une phrase inventée.
 */
function lireTendance(detail: string): { nom: string; raison: string; phase: string } | null {
  const bouts = detail.split(":");
  if (bouts.length < 3) return null;
  const [id, raison, phase] = bouts as [string, string, string];
  return { nom: contenu().parTendance.get(id)?.nom ?? id, raison, phase };
}

/**
 * La précision qui suit le libellé, quand la ligne en porte une. C'est ici que vit la
 * **prévenance** d'⟦A-15⟧ : « ton idée *Console du grenier* s'est périmée » n'apprend rien si
 * on ne dit pas LAQUELLE.
 *
 * Les `detail` du moteur sont des **identifiants** (sujet, événement, tendance) : on les
 * traduit par le contenu, une seule source de vérité, jamais par une table recopiée.
 */
export function detailLisible(ligne: RecapLigne): string | null {
  const d = ligne.detail;
  if (d === null || d === "") return null;
  const c = contenu();
  switch (ligne.cause) {
    case "idee_perimee":
      return `« ${c.parSujet.get(d)?.nom ?? d} »`;
    case "opportunite_expiree": {
      // ⟦C57c · A-174⟧ Le titre court de l'offre (5 mots au plus), le nom entier seulement quand le contenu n'a pas de court.
      const offre = c.parEvenement.get(d);
      return `« ${offre ? titreCourt(offre) : d} »`;
    }
    case "tendance": {
      const t = lireTendance(d);
      return `« ${t?.nom ?? c.parTendance.get(d)?.nom ?? d} »`;
    }
    case "rival": {
      // ⟦A-15 §3⟧ Le moteur écrit `Nom:+gagnes` — `RecapLigne.detail` est une chaîne unique, il
      // n'a pas d'autre endroit où mettre les deux. On le sépare ici plutôt que de laisser
      // « Kévin du Treize:+38 » à l'écran. `lastIndexOf` : un nom de PNJ a le droit d'avoir un
      // deux-points, le séparateur est toujours le dernier.
      const coupe = d.lastIndexOf(":+");
      if (coupe < 0) return d;
      const gagnes = Number(d.slice(coupe + 2));
      if (!Number.isFinite(gagnes)) return d;
      return `${d.slice(0, coupe)} a pris ${nombre(gagnes)} abonné${gagnes > 1 ? "s" : ""}`;
    }
    // ⟦SPEC-FIL §9.4⟧ Le moteur écrit un `detail` déjà lisible (« silence », « ta communauté a
    // partagé », un id de commentaire). Les deux premiers sont des phrases françaises écrites
    // pour l'écran ; un id de commentaire n'a rien à faire sous les yeux du joueur, on le
    // remplace par l'auteur, qui est ce qu'il a réellement lu dans le Fil.
    case "fil": {
      const commentaire = c.parCommentaire.get(d);
      return commentaire ? `« ${commentaire.auteur} »` : d;
    }
    case "nuits_contenues": {
      // Le `detail` est le NOMBRE de nuits. On cite les deux constantes du moteur plutôt que
      // de soustraire à sa place : c'est « décrire tout le calcul » sans en inventer un.
      const n = Number(d);
      if (!Number.isFinite(n) || n <= 0) return null;
      return `${nombre(n)} nuit${n > 1 ? "s" : ""} de tournage à +${SM_NUIT_CONTENUE} ❤ au lieu de +${SM_NUIT} ❤`;
    }
    default:
      return d;
  }
}

/**
 * La phrase d'une ligne **narrative** (`jauge === null`) — celles qui n'ont pas de contrepartie
 * chiffrée mais qui portent le fait à ne pas rater.
 */
export function phraseNarrative(ligne: RecapLigne): string {
  const detail = detailLisible(ligne);
  switch (ligne.cause) {
    case "idee_perimee":
      return `Ton idée ${detail ?? ""} a fait son temps : elle quitte ta main.`.replace("  ", " ");
    case "opportunite_expiree":
      return `La fenêtre ${detail ?? ""} s'est refermée sans toi.`.replace("  ", " ");
    case "tendance": {
      // Ce qui intéresse le joueur n'est pas « il y a eu un changement de phase », c'est **ce
      // que ça veut dire pour lui** : une vague qui démarre, une qu'il faut prendre maintenant,
      // une qui sature, une qui est morte. Le vocabulaire des phases est fermé côté moteur.
      const t = lireTendance(ligne.detail ?? "");
      if (!t) return `La tendance ${detail ?? ""} a changé de phase.`.replace("  ", " ");
      const nom = `« ${t.nom} »`;
      if (t.raison === "rotation") return `Nouvelle vague : ${nom} démarre.`;
      switch (t.phase) {
        case "pic":
          return `${nom} est à son pic — c'est maintenant qu'on la prend.`;
        case "saturee":
          return `${nom} sature : tout le monde s'y est mis.`;
        case "morte":
          return `${nom} est morte, plus personne n'en parle.`;
        default:
          return `${nom} démarre.`;
      }
    }
    case "rival":
      return `${detail ?? "Un autre créateur a avancé"} pendant que tu travaillais.`;
    // ⟦SPEC-FIL §9.4⟧ Les lignes narratives du Fil : « 3 commentaires ont défilé », « une idée
    // dans les commentaires, et la main pleine ». Le moteur les écrit déjà en français — il n'y
    // a rien à traduire, seulement à ne pas les préfixer d'un libellé de cause qui ferait
    // « ta communauté refroidit 3 commentaires ont défilé ».
    case "fil":
      return ligne.detail ?? "Ton Fil a bougé.";
    default:
      return [libelleCause(ligne.cause, "sleep"), detail].filter(Boolean).join(" ");
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Regroupement
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Les lignes du monde, groupées par **jour simulé croissant**, l'ordre du moteur conservé à
 * l'intérieur de chaque jour.
 *
 * ⚠️ **Pourquoi pas `recapParJour` du moteur.** Son groupement est *consécutif* : il ouvre un
 * groupe dès que le jour change d'une ligne à l'autre. Mesuré sur une vraie longue de 48 h, la
 * suite des jours vaut `J3, J6, J4, J5, J6` — les nuits contenues sont datées de l'arrivée et
 * les idées périmées sont purgées **avant** la boucle des frontières. Le helper produirait donc
 * des en-têtes « Jour 6 », puis « Jour 4 »… A-16 demande l'ordre **chronologique** : on trie par
 * jour, et l'on ne garde dans cette section que les causes qui appartiennent réellement à un
 * jour (`SECTION_CAUSE`). Aucun delta n'est touché, c'est de la mise en page.
 */
export function parJourCroissant(lignes: readonly RecapLigne[]): {
  jour: number;
  lignes: RecapLigne[];
}[] {
  const parJour = new Map<number, RecapLigne[]>();
  for (const l of lignes) {
    const groupe = parJour.get(l.jour);
    if (groupe) groupe.push(l);
    else parJour.set(l.jour, [l]);
  }
  return [...parJour.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([jour, lignesDuJour]) => ({
      jour,
      lignes: [...lignesDuJour].sort((a, b) => a.ordre - b.ordre),
    }));
}

/** Les trois sections de l'écran, dans l'ordre où elles se lisent. */
export function sectionner(lignes: readonly RecapLigne[]): {
  travail: RecapLigne[];
  monde: RecapLigne[];
  apres: RecapLigne[];
} {
  const sections = { travail: [] as RecapLigne[], monde: [] as RecapLigne[], apres: [] as RecapLigne[] };
  for (const l of lignes) sections[SECTION_CAUSE[l.cause]].push(l);
  return sections;
}
